import { merge } from 'lodash-es';
import React, { type ComponentProps, useMemo } from 'react';

import type { IconName } from '../../assets/Icon/Icon';
import type { AlignStackPresetProp } from '../../utilities/AlignStack/AlignStack';
import type { GutterProp } from '../../utilities/shared/Gutter';
import type { SpacingScale } from '../../utilities/shared/sizes';
import type { PolymorphicComponentProps } from '../../utilities/types/polymorphicAsProp';
import type { AccessLevelProp } from '../../utilities/useAccessLevelContext';
import type { BetaProp } from '../../utilities/useBeta';
import type { InternalProp } from '../../utilities/useInternal';
import { Icon } from '../../assets/Icon/Icon';
import { ControlSizeProvider } from '../../common/control_size';
import { sizing } from '../../common/sizing';
import { ControlGroup } from '../../controls/ControlGroup/ControlGroup';
import { selectors } from '../../controls/shared/styles';
import { UtilityControl } from '../../controls/UtilityControl/UtilityControl';
import FeatureBadge from '../../formatting/FeatureBadge/FeatureBadge';
import { colors, darkThemeSelector, fontWeights, shadows, styled } from '../../stitches.config';
import { BodySansSizes } from '../../text/Body';
import { LargeSansSizes } from '../../text/Large';
import { Small, SmallSansSizes } from '../../text/Small';
import { Text } from '../../text/Text';
import { AlignStack } from '../../utilities/AlignStack/AlignStack';
import { gutterCSS } from '../../utilities/shared/Gutter';
import { spacingCSS } from '../../utilities/shared/Spacing';
import { HStack } from '../../utilities/Stack/HStack';
import {
  AccessLevel,
  isAccessLevelVisible,
  useAccessLevelContext,
  UserAccessLevelContextProvider,
} from '../../utilities/useAccessLevelContext';
import { Badge } from '../Badge/Badge';
import { Tabs } from '../Tabs/Tabs';
import { LabelTooltip } from '../Tooltip/Tooltip';
import { SectionProvider, useSectionContext } from './utils';

const SectionHeaderIcon = styled(Icon, {
  color: colors.bodyNeutralLight,

  [darkThemeSelector]: {
    color: colors.bodyNeutralDark,
  },

  variants: {
    hasSize: {
      'x-small': {
        width: '$12',
        height: '$12',
      },
      small: {
        width: '$14',
        height: '$14',
      },
      medium: {
        width: '$16',
        height: '$16',
      },
    },
  },
});

const SectionHeaderHeading = styled(Text, {
  display: 'flex',
  width: '100%',
  minWidth: 0,

  variants: {
    size: {
      'x-small': {
        ...SmallSansSizes,
        fontWeight: fontWeights.extraBold,
      },
      small: {
        ...BodySansSizes,
        fontWeight: fontWeights.bold,
      },
      medium: {
        ...LargeSansSizes,
        fontWeight: fontWeights.bold,
      },
    },
  },
});

const SectionHeaderDescription = styled(Small, {
  display: 'flex',
  width: '100%',
  minWidth: 0,
});

const SectionHeaderCopy = styled('div', {
  display: 'flex',
  flexDirection: 'column',
  width: '100%',
  minWidth: 0,
});

const SectionHeaderActions = styled(ControlGroup);

const SectionHeaderTabs = styled(Tabs, {
  minHeight: 'auto',
});

const SectionHeaderStart = styled('div', {
  minWidth: 0,
  position: 'relative',
  zIndex: 1,
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  gap: '$8',
});

const SectionHeaderEnd = styled('div', {
  minWidth: 0,
  position: 'relative',
  zIndex: 1,
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  gap: '$8',
});

const SectionHeaderContainer = styled('div', {
  minWidth: 0,
  position: 'relative',
  display: 'flex',
  flexDirection: 'row',
  justifyContent: 'space-between',

  variants: {
    size: {
      'x-small': {
        gap: '$6',
        minHeight: '$20',
      },
      small: {
        minHeight: '$24',
        gap: '$8',
      },
      medium: {
        minHeight: '$24',
        gap: '$8',
      },
    },
    relation: {
      stacked: {},
      standalone: {},
    },
  },

  compoundVariants: [
    {
      size: 'x-small',
      relation: 'stacked',
      css: {
        padding: `$4 ${sizing.contentSides}`,
      },
    },
    {
      size: 'small',
      relation: 'stacked',
      css: {
        padding: `$8 ${sizing.sides}`,
      },
    },
    {
      size: 'small',
      relation: 'standalone',
      css: {
        padding: `$8 ${sizing.contentSides}`,
      },
    },
    {
      size: 'medium',
      relation: 'stacked',
      css: {
        padding: `$8 ${sizing.sides}`,
      },
    },
    {
      size: 'medium',
      relation: 'standalone',
      css: {
        padding: `$8 ${sizing.contentSides}`,
      },
    },
  ],
});

export type SectionHeaderProps = {
  /**
   * Access level for the section
   */
  accessLevel?: AccessLevel;
  /**
   * Provide any actions.
   */
  actions?: React.ReactNode;
  /**
   * Boolean to signify the section as beta.
   */
  beta?: BetaProp;
  /**
   * Boolean to collapse the section.
   */
  collapsed?: boolean;
  /**
   * Provide a count for your pane.
   */
  count?: number;
  /**
   * Provide a description for your pane.
   */
  description?: React.ReactNode;
  /**
   * Provide a heading for your pane.
   */
  heading?: React.ReactNode;
  /**
   * Provide an icon for your pane.
   */
  icon?: IconName;
  /**
   * The ID of the section.
   */
  id?: SectionIDProp;
  /**
   * Boolean to signify the section as internal.
   */
  internal?: InternalProp;
  /**
   * The relation of the section.
   */
  relation?: SectionPropRelation;
  /**
   * The size of the section header.
   */
  size?: SectionPropSize;
  /**
   * Provide any tabs you want to display.
   */
  tabs?: React.ReactNode;
  /**
   * Provide a tooltip for the heading.
   */
  tooltip?: React.ReactNode;
  style?: React.CSSProperties;
};

export function SectionHeader({
  accessLevel: accessLevelProp,
  actions,
  beta,
  count,
  collapsed,
  description,
  heading,
  icon,
  id,
  internal,
  relation,
  size,
  tabs,
  tooltip,
  ...remaining
}: SectionHeaderProps) {
  const accessLevel = useAccessLevelContext(accessLevelProp);
  const sectionContext = useSectionContext();
  const hasLockup = icon || heading;

  let headingPreset: AlignStackPresetProp = 'large';
  if (sectionContext.size === 'x-small') {
    headingPreset = 'small';
  } else if (sectionContext.size === 'small') {
    headingPreset = 'body';
  }

  const headingRender = (
    <SectionHeaderHeading context="heading" variant="neutral" size={sectionContext.size}>
      {heading}
    </SectionHeaderHeading>
  );

  return (
    <UserAccessLevelContextProvider value={accessLevel}>
      <SectionHeaderContainer
        relation={sectionContext.relation}
        size={sectionContext.size}
        {...remaining}
      >
        <SectionHeaderStart>
          {hasLockup && (
            <AlignStack
              as={sectionContext.collapsible ? 'button' : 'div'}
              onClick={
                sectionContext.collapsible
                  ? () => sectionContext.setCollapsed(!sectionContext.collapsed)
                  : undefined
              }
              tabIndex={sectionContext.collapsible ? -1 : undefined}
              gap={sectionContext.size === 'x-small' ? 4 : 6}
              preset={sectionContext.size === 'x-small' ? 'small' : 'large'}
              start={
                (sectionContext.collapsible || icon) && (
                  <HStack
                    align="center"
                    spacing={sectionContext.size === 'x-small' ? 4 : 6}
                    wrap="nowrap"
                  >
                    {sectionContext.collapsible && (
                      <UtilityControl
                        icon={sectionContext.collapsed ? 'chevron-right' : 'chevron-down'}
                        label={sectionContext.collapsed ? 'Expand' : 'Collapse'}
                        variant="neutral"
                      />
                    )}
                    {icon && <SectionHeaderIcon icon={icon} hasSize={sectionContext.size} />}
                  </HStack>
                )
              }
              end={
                (count || accessLevel) && (
                  <>
                    {count != null && (
                      <Badge
                        ends="card"
                        size={sectionContext.size === 'x-small' ? 'x-small' : 'small'}
                        variant="neutral"
                      >
                        {count}
                      </Badge>
                    )}
                    {accessLevel && isAccessLevelVisible(accessLevel) && (
                      <FeatureBadge size="x-small" type={accessLevel} />
                    )}
                  </>
                )
              }
            >
              {(heading || description) && (
                <SectionHeaderCopy>
                  {heading &&
                    (tooltip ? (
                      <LabelTooltip contents={tooltip} preset={headingPreset} width="full">
                        {headingRender}
                      </LabelTooltip>
                    ) : (
                      headingRender
                    ))}
                  {description && (
                    <SectionHeaderDescription>{description}</SectionHeaderDescription>
                  )}
                </SectionHeaderCopy>
              )}
            </AlignStack>
          )}
          {tabs && <SectionHeaderTabs>{tabs}</SectionHeaderTabs>}
        </SectionHeaderStart>
        <SectionHeaderEnd>
          {actions && (
            <ControlSizeProvider value="small">
              <SectionHeaderActions
                relation="separate"
                size={sectionContext.size === 'x-small' ? 'x-small' : 'small'}
              >
                {actions}
              </SectionHeaderActions>
            </ControlSizeProvider>
          )}
        </SectionHeaderEnd>
      </SectionHeaderContainer>
    </UserAccessLevelContextProvider>
  );
}

const SectionContentContainer = styled('div', {
  position: 'relative',
  display: 'flex',
  flexDirection: 'column',

  variants: {
    collapsed: {
      true: {
        display: 'none',
      },
      false: {},
    },
    relation: {
      stacked: {},
      standalone: {},
    },
    size: {
      'x-small': {},
      small: {},
      medium: {},
    },
  },
});

export type SectionContentProps = {
  /**
   * Pass in any content as `children`.
   */
  children?: React.ReactNode;
  /**
   * Boolean to collapse the section.
   */
  collapsed?: boolean;
  /**
   * Set whether there should be a gutter or not around the children.
   */
  gutter?: GutterProp;
  /**
   * The ID of the section.
   */
  id?: SectionIDProp;
  /**
   * The relation of the section.
   */
  relation?: SectionPropRelation;
  /**
   * The size of the section.
   */
  size?: SectionPropSize;
  /**
   * The spacing of the section.
   */
  spacing?: SpacingScale;
} & ComponentProps<typeof SectionContentContainer>;

export function SectionContent({
  children,
  collapsed,
  gutter = 'none',
  id,
  relation,
  size,
  spacing = 0,
  ...remaining
}: SectionContentProps) {
  const sectionContext = useSectionContext();
  return (
    <SectionContentContainer
      collapsed={sectionContext.collapsed}
      relation={sectionContext.relation}
      size={sectionContext.size}
      css={merge(
        gutterCSS({
          context: sectionContext.relation === 'stacked' ? 'section' : 'content',
          gutter,
        }),
        spacingCSS(spacing),
      )}
      {...remaining}
    >
      {children}
    </SectionContentContainer>
  );
}

const SectionFooterHelper = styled(Small);

const SectionFooterEnds = styled('div', {
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  gap: '$8',
  padding: '$6 0',
});

const SectionFooterStart = styled(SectionFooterEnds);

const SectionFooterEnd = styled(SectionFooterEnds);

const SectionFooterContainer = styled('div', {
  position: 'relative',
  hStack: '$8',
  justifyContent: 'space-between',
  minHeight: '$36',

  variants: {
    collapsed: {
      true: {
        display: 'none',
      },
      false: {},
    },
    relation: {
      stacked: {
        padding: sizing.sidesOnly,

        '&:before': {
          left: sizing.sides,
          right: sizing.sides,
        },
      },
      standalone: {
        padding: sizing.contentSidesOnly,

        '&:before': {
          left: sizing.contentSides,
          right: sizing.contentSides,
        },
      },
    },
  },
});

export type SectionFooterProps = {
  /**
   * Boolean to collapse the section.
   */
  collapsed?: boolean;
  /**
   * Provide any actions for the right-side.
   */
  endActions?: React.ReactNode;
  /**
   * Any helper text that should be shown.
   */
  helper?: React.ReactNode;
  /**
   * The ID of the section.
   */
  id?: SectionIDProp;
  /**
   * The relation of the section.
   */
  relation?: SectionPropRelation;
  /**
   * Provide any actions for the left-side.
   */
  startActions?: React.ReactNode;
};

export function SectionFooter({
  collapsed,
  endActions,
  helper,
  id,
  relation,
  startActions,
  ...remaining
}: SectionFooterProps) {
  const sectionContext = useSectionContext();
  return (
    <SectionFooterContainer
      collapsed={sectionContext.collapsed}
      relation={sectionContext.relation}
      {...remaining}
    >
      <SectionFooterStart>
        {startActions && (
          <ControlGroup size="small" relation="separate">
            {startActions}
          </ControlGroup>
        )}
        {helper && <SectionFooterHelper>{helper}</SectionFooterHelper>}
      </SectionFooterStart>
      <SectionFooterEnd>
        {endActions && (
          <ControlGroup size="small" relation="separate">
            {endActions}
          </ControlGroup>
        )}
      </SectionFooterEnd>
    </SectionFooterContainer>
  );
}

const SectionTargetContent = styled('div', {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: '100%',
  borderRadius: '$10',
  color: colors.linkAlternativeInitialLight,
  fontWeight: fontWeights.bold,

  [darkThemeSelector]: {
    color: colors.linkAlternativeInitialDark,
  },

  [selectors.hover]: {
    color: colors.linkAlternativeInteractiveLight,

    [darkThemeSelector]: {
      color: colors.linkAlternativeInteractiveDark,
    },
  },

  '&:first-child': {
    borderRadiusTop: '$8',
  },

  '&:last-child': {
    borderRadiusBottom: '$8',
  },

  variants: {
    size: {
      'x-small': {
        padding: '$4 $8',
        ...SmallSansSizes,
      },
      small: {
        padding: '$4 $8',
        ...BodySansSizes,
      },
      medium: {
        padding: '$6 $12',
        ...BodySansSizes,
      },
    },
  },
});

const SectionTargetContainer = styled('a', {
  display: 'flex',
  width: '100%',
  cursor: 'pointer',

  [selectors.focus]: {
    outline: 'none',

    [`& ${SectionTargetContent}`]: {
      boxShadow: shadows.focusRingLight,

      [darkThemeSelector]: {
        boxShadow: shadows.focusRingDark,
      },
    },
  },

  variants: {
    collapsed: {
      true: {
        display: 'none',
      },
      false: {},
    },
    relation: {
      standalone: {},
      stacked: {},
    },
    size: {
      'x-small': {
        padding: `$4 ${sizing.contentSides}`,
      },
      small: {
        padding: `$4 ${sizing.sides}`,
      },
      medium: {
        padding: `$4 ${sizing.sides}`,
      },
    },
  },

  compoundVariants: [
    {
      relation: 'standalone',
      size: 'x-small',
      css: {
        padding: `$4 ${sizing.contentSides}`,
      },
    },
    {
      relation: 'standalone',
      size: 'small',
      css: {
        padding: `$4 ${sizing.contentSides}`,
      },
    },
    {
      relation: 'standalone',
      size: 'medium',
      css: {
        padding: `$6 ${sizing.contentSides}`,
      },
    },
    {
      relation: 'stacked',
      size: 'x-small',
      css: {
        padding: `$4 ${sizing.contentSides}`,
      },
    },
    {
      relation: 'stacked',
      size: 'small',
      css: {
        padding: `$6 ${sizing.sides}`,
      },
    },
    {
      relation: 'stacked',
      size: 'medium',
      css: {
        padding: `$6 ${sizing.sides}`,
      },
    },
  ],
});

interface SectionTargetProps {
  /**
   * Pass in any content as `children`.
   */
  children: React.ReactNode;
  /**
   * Boolean to collapse the section.
   */
  collapsed?: boolean;
  /**
   * The ID of the section.
   */
  id?: SectionIDProp;
  /**
   * Click handler for the section.
   */
  onClick?: React.MouseEventHandler<HTMLButtonElement>;
  /**
   * The relation of the section.
   */
  relation?: SectionPropRelation;
  /**
   * The size of the section.
   */
  size?: SectionPropSize;
}

export function SectionTarget<Tag extends React.ElementType>({
  as = 'div' as Tag,
  children,
  collapsed,
  id,
  onClick,
  relation,
  size,
  ref,
  ...remaining
}: PolymorphicComponentProps<Tag, SectionTargetProps>) {
  const sectionContext = useSectionContext();
  return (
    <SectionTargetContainer
      {...remaining}
      ref={ref}
      tabIndex={0}
      as={onClick ? 'button' : as}
      onClick={onClick}
      collapsed={sectionContext.collapsed}
      relation={sectionContext.relation}
      size={sectionContext.size}
    >
      <SectionTargetContent size={sectionContext.size}>{children}</SectionTargetContent>
    </SectionTargetContainer>
  );
}

const SectionContainer = styled('div', {
  position: 'relative',
  zIndex: 1,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'stretch',
  width: '100%',

  variants: {
    siblingAware: {
      true: {},
      false: {},
    },
    bordersAlways: {
      true: {},
      false: {},
    },
    relation: {
      stacked: {},
      standalone: {
        borderRadius: '$8',
      },
    },
    size: {
      'x-small': {},
      small: {},
      medium: {},
    },
  },

  compoundVariants: [
    {
      relation: 'stacked',
      siblingAware: true,
      css: {
        position: 'relative',

        '&::before, &::after': {
          content: '',
          position: 'absolute',
          display: 'block',
          height: '$1',
          backgroundColor: colors.strokeNeutralLight,

          [darkThemeSelector]: {
            backgroundColor: colors.strokeNeutralDark,
          },
        },

        '&::before': {
          top: '-0.5px',
        },

        '&::after': {
          bottom: '-0.5px',
        },

        '&:first-child::before': {
          display: 'none',
        },

        '&:last-child::after': {
          display: 'none',
        },
      },
    },
    {
      relation: 'stacked',
      siblingAware: true,
      bordersAlways: true,
      css: {
        '&:first-child::before': {
          display: 'block',
        },

        '&:last-child::after': {
          display: 'block',
        },
      },
    },
    {
      relation: 'stacked',
      siblingAware: true,
      size: 'x-small',
      css: {
        padding: '$8 0',

        '&::before, &::after': {
          right: sizing.contentSides,
          left: sizing.contentSides,
        },
      },
    },
    {
      relation: 'stacked',
      siblingAware: true,
      size: 'small',
      css: {
        padding: '$8 0',

        '&::before, &::after': {
          right: sizing.sides,
          left: sizing.sides,
        },
      },
    },
    {
      relation: 'stacked',
      siblingAware: true,
      size: 'medium',
      css: {
        padding: '$12 0',

        '&::before, &::after': {
          right: sizing.sides,
          left: sizing.sides,
        },
      },
    },
  ],
});

export type SectionIDProp = string;
export type SectionPropRelation = 'stacked' | 'standalone';
export type SectionPropSize = 'x-small' | 'small' | 'medium';

export type SectionProps = {
  /**
   * Access level for the section.
   */
  accessLevel?: AccessLevelProp;
  /**
   * Pass in any content as `children`.
   */
  children?: React.ReactNode;
  /**
   * Boolean to collapse the section.
   */
  collapsible?: boolean;
  /**
   * The ID of the section.
   */
  id?: SectionIDProp;
  /**
   * Boolean to show internal-only styles.
   */
  internal?: InternalProp;
  /**
   * Determines how the section is related to its siblings.
   */
  relation?: SectionPropRelation;
  /**
   * The size of the section.
   */
  size?: SectionPropSize;
} & ComponentProps<typeof SectionContainer>;

export function Section({
  accessLevel: accessLevelProp,
  children,
  collapsible = false,
  id = '',
  internal,
  relation = 'standalone',
  siblingAware = true,
  size = 'medium',
  ...remaining
}: SectionProps) {
  const sectionContext = useSectionContext();

  const accessLevel = useMemo(() => {
    if (accessLevelProp) return accessLevelProp;
    if (internal) return AccessLevel.Internal;
    return undefined;
  }, [accessLevelProp, internal]);

  return (
    <SectionContainer
      relation={sectionContext.relation}
      siblingAware={siblingAware}
      size={sectionContext.size}
      {...remaining}
    >
      <SectionProvider
        id={id}
        collapsible={collapsible}
        relation={sectionContext.relation ?? relation}
        size={sectionContext.size ?? size}
      >
        <UserAccessLevelContextProvider value={accessLevel}>
          {children}
        </UserAccessLevelContextProvider>
      </SectionProvider>
    </SectionContainer>
  );
}

const SectionsContainer = styled('div', {
  display: 'flex',
  flexDirection: 'column',
  width: '100%',
});

type SectionsProps = {
  children: React.ReactNode;
  gap?: SpacingScale;
  gutter?: GutterProp;
  relation?: SectionPropRelation;
  size?: SectionPropSize;
} & ComponentProps<typeof SectionsContainer>;

export function Sections({
  children,
  gap = 0,
  gutter = 'none',
  relation = 'stacked',
  size = 'medium',
  ...remaining
}: SectionsProps) {
  return (
    <SectionsContainer
      css={merge(gutterCSS({ context: 'section', gutter }), spacingCSS(gap))}
      {...remaining}
    >
      <SectionProvider relation={relation} size={size}>
        {children}
      </SectionProvider>
    </SectionsContainer>
  );
}
